import React, { createContext, FC, ReactElement, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AlertColor as Color } from '@mui/material/Alert';

import Toast from '../components/Toast/Toast';
import { hideToast, selectToast, showToast } from '../store/slices/toastSlice';

export interface ToastOptionType {
  type?: Color;
  autoHideDuration?: number;
}

export type ToastFunction = (message: string | string[], option?: ToastOptionType) => void;

export interface ToastType {
  alert: ToastFunction;
  success: ToastFunction;
  error: ToastFunction;
}

export const ToastContext = createContext<ToastType>({
  alert: () => {},
  success: () => {},
  error: () => {},
});

export const ToastProvider: FC<{ children: ReactElement }> = ({ children }) => {
  const dispatch = useDispatch();
  const { show, message, type, autoHideDuration } = useSelector(selectToast);

  const handleToast = (toastMessage: string | string[], toastOption?: ToastOptionType) => {
    dispatch(
      showToast({
        message: toastMessage,
        type: toastOption?.type,
        autoHideDuration: toastOption?.autoHideDuration,
      }),
    );
  };

  const handleClose = () => {
    dispatch(hideToast());
  };

  return (
    <ToastContext.Provider
      value={{
        alert: handleToast,
        success: (toastMessage: string | string[], toastOption?: ToastOptionType) => {
          const op: ToastOptionType = {
            ...toastOption,
            type: 'success',
          };
          handleToast(toastMessage, op);
        },
        error: (toastMessage: string | string[], toastOption?: ToastOptionType) => {
          const op: ToastOptionType = {
            ...toastOption,
            type: 'error',
          };
          handleToast(toastMessage, op);
        },
      }}
    >
      <Toast
        show={show}
        message={message}
        handleClose={handleClose}
        autoHideDuration={autoHideDuration}
        type={type}
      />
      {children}
    </ToastContext.Provider>
  );
};

export const useToast = () => useContext(ToastContext);
